'use client';

import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { getApiErrorTranslationKey } from '@/lib/api/error-codes';

// Custom event for API errors
export const API_ERROR_EVENT = 'api-error-toast';

export interface ApiErrorEvent {
  code?: string;
  message: string;
}

// Function to dispatch error events (called from QueryClient)
export function dispatchApiError(error: ApiErrorEvent) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(API_ERROR_EVENT, { detail: error }));
  }
}

// React component that listens for errors and shows translated toasts
export function ApiErrorToastProvider({ children }: { children: React.ReactNode }) {
  const tErrors = useTranslations('errors');

  useEffect(() => {
    const handleApiError = (event: CustomEvent<ApiErrorEvent>) => {
      const { code, message } = event.detail;

      // Try to show translated message if error code exists
      const translationKey = code ? getApiErrorTranslationKey(code) : undefined;
      if (translationKey) {
        toast.error(tErrors(translationKey as Parameters<typeof tErrors>[0]));
      } else {
        // Fallback to original message or generic error
        toast.error(message || tErrors('generic.unknown'));
      }
    };

    window.addEventListener(API_ERROR_EVENT, handleApiError as EventListener);
    return () => {
      window.removeEventListener(API_ERROR_EVENT, handleApiError as EventListener);
    };
  }, [tErrors]);

  return <>{children}</>;
}
